import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Grid,
  Group,
  Modal,
  Paper,
  rem,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconBarcode,
  IconBuildingWarehouse,
  IconCheck,
  IconDownload,
  IconFileUpload,
  IconHistory,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { repositoryPos } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { anyAsciiCode } from "../../../_base/helper/AnyAscii";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getInventorySelectPos,
  getSerialStatusSelect,
} from "../../../service/getSelectApi";

type ScanSource = "lookup" | "manual";
type LookupStatus = "pending" | "success" | "error" | "manual";

interface LookupApiResponse {
  id?: number | string;
  maVt?: string | null;
  tenVt?: string | null;
  serial?: string | null;
  serialCode?: string | null;
  statusMessage?: string | null;
  tinhTrang?: string | null;
  serialStatus?: string | null;
  ghiChuTinhTrang?: string | null;
  note?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  storageDays?: number | null;
  subInvCode?: string | null;
  sumQuantity?: number | null;
  difference?: number | null;
  quantity?: number | null;
  thoiGianLuuKho?: number | null;
  slDaQuet?: number | null;
  chenhLech?: number | null;
  warningMessage?: string | null;
}

type LookupApiResult = LookupApiResponse | LookupApiResponse[];

interface ScanRow {
  id: string;
  sourceType: ScanSource;
  serial: string;
  itemCode: string;
  itemName: string;
  condition: string;
  conditionNote: string;
  stockAge: number;
  expectedQuantity: number;
  scanCount: number;
  variance: number;
  lookupStatus: LookupStatus;
  errorMessage: string;
  warningMessage: string;
  sourceRows: number[];
  isSaved: boolean;
  saveId?: number | string;
}

interface StocktakeState {
  completedAt: string | null;
  importedFileName: string;
  subInvCode: string;
  kyKiemKe: number;
  rows: ScanRow[];
}

const STORAGE_KEY = "warehouse_serial_stocktake_v2";

const STOCKTAKING_ENDPOINTS = {
  lookup: "/api/v1/TblInventorySerialOnHand/scan-serial",
  save: "/api/v1/TblInventorySerialOnHand/create-scan-result",
};

const DEFAULT_CONDITION_OPTIONS = [
  { value: "Hàng bán bình thường", label: "Hàng bán bình thường" },
  { value: "Vỏ cũ, rách, xước", label: "Vỏ cũ, rách, xước" },
  { value: "Hàng gãy, vỡ", label: "Hàng gãy, vỡ" },
  { value: "Hàng hủy", label: "Hàng hủy" },
  { value: "Hàng bảo hành về", label: "Hàng bảo hành về" },
];

const getCurrentMonth = () => new Date().getMonth() + 1;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const month = index + 1;
  return {
    value: String(month),
    label: `Tháng ${month}`,
  };
});

const normalizeInventoryMonth = (value: unknown) => {
  const month = Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12
    ? month
    : getCurrentMonth();
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#d9e3f7",
  fontWeight: 700,
  fontSize: 14,
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "8px 10px",
};

const valueCellStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  minHeight: 44,
  display: "flex",
  alignItems: "center",
  padding: "4px 8px",
};

const dividerStyle: React.CSSProperties = {
  backgroundColor: "#fde9dd",
  height: 26,
};

const singleLineGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns:
    "260px minmax(180px,1.1fr) 90px minmax(120px,0.8fr) 90px minmax(180px,1fr) 110px minmax(160px,0.9fr) 140px minmax(220px,1.2fr)",
  minWidth: 1530,
};

const actionButtonStyle: React.CSSProperties = {
  minWidth: 150,
  height: 36,
};

const createRowId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const normalizeSerial = (value: string) =>
  anyAsciiCode(value ?? "").trim().toUpperCase();
const normalizeItemCode = (value: string) =>
  anyAsciiCode(value ?? "").trim().toUpperCase();

const recalculateGroupedVariance = (rows: ScanRow[]) => {
  const grouped = new Map<string, { scanCount: number; expectedQuantity: number }>();

  rows.forEach((row) => {
    const key = normalizeItemCode(row.itemCode);
    if (!key) return;
    const current = grouped.get(key) || { scanCount: 0, expectedQuantity: 0 };
    current.scanCount += Math.max(0, row.scanCount || 0);
    current.expectedQuantity = Math.max(
      current.expectedQuantity,
      Math.max(0, row.expectedQuantity || 0)
    );
    grouped.set(key, current);
  });

  return rows.map((row) => {
    if (row.lookupStatus === "success") {
      return row;
    }

    const key = normalizeItemCode(row.itemCode);
    if (!key) return row;
    const current = grouped.get(key);
    if (!current) return row;
    return {
      ...row,
      variance: current.scanCount - current.expectedQuantity,
    };
  });
};

const parseNumber = (value: string | undefined) => {
  if (!value) {
    return 1;
  }

  const parsed = Number(value.trim().replace(",", "."));

  if (Number.isNaN(parsed) || parsed <= 0) {
    return 1;
  }

  return parsed;
};

const readFileText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve((event.target?.result as string) ?? "");
    };

    reader.onerror = () => reject(new Error("Không thể đọc file"));
    reader.readAsText(file, "utf-8");
  });

const readExcelWorkbook = (file: File) =>
  new Promise<XLSX.WorkBook>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const buffer = event.target?.result;

        if (!(buffer instanceof ArrayBuffer)) {
          reject(new Error("Invalid Excel buffer"));
          return;
        }

        const workbook = XLSX.read(buffer, { type: "array" });
        resolve(workbook);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Không thể đọc file Excel"));
    reader.readAsArrayBuffer(file);
  });

const getSheetTextFromWorkbook = (
  workbook: XLSX.WorkBook,
  sheetName: string
) => {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    return "";
  }

  return (
    XLSX.utils.sheet_to_csv(worksheet, {
      FS: ",",
      RS: "\n",
      strip: true,
      blankrows: false,
    }) || ""
  );
};

const buildTemplateSheet = () => {
  const rows = [
    ["Serial", "Số lượng", "Tình trạng", "Ghi chú tình trạng"],
    ["20240188942", 1, "Hàng bán bình thường", ""],
    ["20240188939", 2, "Vỏ cũ, rách, xước", "Máy quét trả về số lượng 2"],
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = rows[0].map((header) => ({
    wch: String(header).length + 12,
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "KiemKeSerial");
  XLSX.writeFile(workbook, "Mau_storecheck_kiem_ke_serial.xlsx");
};

const createInitialState = (): StocktakeState => ({
  completedAt: null,
  importedFileName: "",
  subInvCode: "",
  kyKiemKe: getCurrentMonth(),
  rows: [],
});

const readStoredState = (): StocktakeState => {
  try {
    const rawValue = localStorage.getItem(STORAGE_KEY);

    if (!rawValue) {
      return createInitialState();
    }

    const parsed = JSON.parse(rawValue) as Partial<StocktakeState>;

    return {
      completedAt: parsed.completedAt || null,
      importedFileName: parsed.importedFileName || "",
      subInvCode: parsed.subInvCode || "",
      kyKiemKe: normalizeInventoryMonth(parsed.kyKiemKe),
      rows: Array.isArray(parsed.rows) ? parsed.rows : [],
    };
  } catch (error) {
    console.error(error);
    return createInitialState();
  }
};

const createLookupRow = ({
  serial,
  scanCount,
  condition,
  conditionNote,
  sourceRows,
}: {
  serial: string;
  scanCount: number;
  condition: string;
  conditionNote: string;
  sourceRows: number[];
}): ScanRow => ({
  id: createRowId(),
  sourceType: "lookup",
  serial,
  itemCode: "",
  itemName: "",
  condition,
  conditionNote,
  stockAge: 0,
  expectedQuantity: 0,
  scanCount,
  variance: scanCount,
  lookupStatus: "pending",
  errorMessage: "",
  warningMessage: "",
  sourceRows,
  isSaved: false,
});

const getLookupDataItem = (payload?: LookupApiResult | null) => {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }

  return payload;
};

const getLookupDataList = (payload?: LookupApiResult | null) => {
  if (!payload) {
    return [];
  }

  return Array.isArray(payload) ? payload : [payload];
};

const pickText = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const pickNumber = (...values: Array<number | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return 0;
};

const hasPosInventoryData = (data: LookupApiResponse) => {
  const itemCode = pickText(data.itemCode, data.maVt);
  const itemName = pickText(data.itemName, data.tenVt);
  return Boolean(itemCode && itemName);
};

const SerialStocktake = () => {
  const navigate = useNavigate();
  const [screenState, setScreenState] = React.useState<StocktakeState>(() =>
    readStoredState()
  );
  const [conditionOptions, setConditionOptions] = React.useState(
    DEFAULT_CONDITION_OPTIONS
  );
  const [warehouseOptions, setWarehouseOptions] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [serialInput, setSerialInput] = React.useState("");
  const [serialCondition, setSerialCondition] = React.useState(
    DEFAULT_CONDITION_OPTIONS[0].value
  );
  const [serialConditionNote, setSerialConditionNote] = React.useState("");
  const [isResolvingItemName, setIsResolvingItemName] = React.useState(false);
  const [importWorkbook, setImportWorkbook] = React.useState<XLSX.WorkBook | null>(
    null
  );
  const [importSheetNames, setImportSheetNames] = React.useState<string[]>([]);
  const [selectedImportSheet, setSelectedImportSheet] = React.useState("");
  const [pendingImportFileName, setPendingImportFileName] = React.useState("");
  const [missingInput, setMissingInput] = React.useState({
    serial: "",
    itemCode: "",
    itemName: "",
    condition: DEFAULT_CONDITION_OPTIONS[2].value,
    conditionNote: "",
  });
  const [visible, { open, close }] = useDisclosure(false);
  const [sheetModalOpened, { open: openSheetModal, close: closeSheetModal }] =
    useDisclosure(false);
  const resetFileRef = React.useRef<() => void>(null);
  const closeImportSheetModal = React.useCallback(() => {
    closeSheetModal();
    setImportWorkbook(null);
    setImportSheetNames([]);
    setSelectedImportSheet("");
    setPendingImportFileName("");
  }, [closeSheetModal]);

  const goToSerialHistory = React.useCallback(
    (serial: string) => {
      const normalizedSerial = normalizeSerial(serial);

      if (!normalizedSerial) {
        return;
      }

      navigate(
        `/warehouse-config/barcode-history?serial=${encodeURIComponent(normalizedSerial)}`
      );
    },
    [navigate]
  );

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(screenState));
  }, [screenState]);

  const fetchWarehouseOptions = React.useCallback(async () => {
    if (warehouseOptions.length > 0) {
      return;
    }

    const response = await getInventorySelectPos();

    const mappedOptions = (response || [])
      .filter((item) => item?.code && item?.name)
      .map((item) => ({
        value: String(item.code),
        label: `(${item.code}) ${item.name}`,
      }));

    setWarehouseOptions(mappedOptions);
  }, [warehouseOptions.length]);

  React.useEffect(() => {
    void fetchWarehouseOptions();
  }, [fetchWarehouseOptions]);

  const fetchSerialStatusOptions = React.useCallback(async () => {
    const response = await getSerialStatusSelect();

    const mappedOptions = (response || [])
      .filter((item) => item?.value != null && item?.text != null)
      .map((item) => ({
        value: String(item.text),
        label: String(item.text),
      }));

    if (mappedOptions.length > 0) {
      setConditionOptions(mappedOptions);
      setSerialCondition((prev) =>
        prev || mappedOptions[0]?.value || DEFAULT_CONDITION_OPTIONS[0].value
      );
      setMissingInput((prev) => ({
        ...prev,
        condition:
          prev.condition ||
          mappedOptions[2]?.value ||
          mappedOptions[0]?.value ||
          DEFAULT_CONDITION_OPTIONS[2].value,
      }));
    }
  }, []);

  React.useEffect(() => {
    void fetchSerialStatusOptions();
  }, [fetchSerialStatusOptions]);

  const extractItemName = React.useCallback((payload: any): string => {
    if (!payload) return "";
    if (typeof payload === "string") return payload.trim();
    if (Array.isArray(payload)) return extractItemName(payload[0]);
    return pickText(payload.itemName, payload.productName, payload.tenVt, payload.name);
  }, []);

  const resolveItemByCode = React.useCallback(
    async (itemCode: string) => {
      const normalizedCode = itemCode.trim().toUpperCase();
      if (!normalizedCode) return null;
      const url = `/api/v1/TblItem/get-item-name?itemCode=${encodeURIComponent(
        normalizedCode
      )}`;
      const response = await repositoryPos.get<MessageResponse<any>>(url);
      if (!response?.success) return null;
      const itemName = extractItemName(response.data);
      return itemName || null;
    },
    [extractItemName]
  );

  const upsertRows = React.useCallback((rowsToAppend: ScanRow[]) => {
    if (!rowsToAppend.length) {
      return;
    }

    setScreenState((prev) => {
      const nextRows = [...prev.rows];

      rowsToAppend.forEach((incomingRow) => {
        const existingIndex = nextRows.findIndex(
          (item) => normalizeSerial(item.serial) === normalizeSerial(incomingRow.serial)
        );

        if (existingIndex === -1) {
          nextRows.unshift(incomingRow);
          return;
        }

        const currentRow = nextRows[existingIndex];
        const mergedScanCount = currentRow.scanCount + incomingRow.scanCount;
        const expectedQuantity =
          currentRow.expectedQuantity > 0
            ? currentRow.expectedQuantity
            : incomingRow.expectedQuantity;
        const variance = mergedScanCount - expectedQuantity;

        nextRows[existingIndex] = {
          ...currentRow,
          itemCode: incomingRow.itemCode || currentRow.itemCode,
          itemName: incomingRow.itemName || currentRow.itemName,
          condition: incomingRow.condition || currentRow.condition,
          conditionNote: incomingRow.conditionNote || currentRow.conditionNote,
          stockAge: incomingRow.stockAge || currentRow.stockAge,
          expectedQuantity,
          lookupStatus: incomingRow.lookupStatus || currentRow.lookupStatus,
          errorMessage: incomingRow.errorMessage || currentRow.errorMessage,
          warningMessage: incomingRow.warningMessage || currentRow.warningMessage,
          scanCount: mergedScanCount,
          variance,
          sourceRows: Array.from(
            new Set([...currentRow.sourceRows, ...incomingRow.sourceRows])
          ),
          isSaved: false,
          saveId: undefined,
        };
      });

      return {
        ...prev,
        rows: recalculateGroupedVariance(nextRows),
      };
    });
  }, []);

  const parseStorecheckContent = React.useCallback((content: string) => {
    const normalizedText = content.replace(/^\uFEFF/, "");
    const lines = normalizedText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const serialMap = new Map<
      string,
      {
        serial: string;
        scanCount: number;
        condition: string;
        conditionNote: string;
        sourceRows: number[];
      }
    >();

    lines.forEach((line, index) => {
      const cells = line
        .split(/[,;\t|]/)
        .map((item) => item.trim())
        .filter((item) => item !== "");

      if (!cells.length) {
        return;
      }

      const serial = normalizeSerial(cells[0]);

      if (!serial || serial === "SERIAL") {
        return;
      }

      const quantity = parseNumber(cells[1]);
      const condition = cells[2] || DEFAULT_CONDITION_OPTIONS[0].value;
      const note = cells.slice(3).join(" ").trim();

      const current = serialMap.get(serial);

      if (current) {
        current.scanCount += quantity;
        current.condition = current.condition || condition;
        current.conditionNote = current.conditionNote || note;
        current.sourceRows.push(index + 1);
      } else {
        serialMap.set(serial, {
          serial,
          scanCount: quantity,
          condition,
          conditionNote: note,
          sourceRows: [index + 1],
        });
      }
    });

    return Array.from(serialMap.values()).map((item) =>
      createLookupRow(item)
    );
  }, []);

  const applyImportedRows = async (
    parsedRows: ScanRow[],
    importedFileName: string
  ) => {
    if (!parsedRows.length) {
      NotificationExtension.Warn("Không đọc được serial từ file storecheck.");
      return;
    }

    let nextLookupRows = parsedRows;

    if (screenState.subInvCode) {
      const lookedUpRows = await lookupByImportedFile(parsedRows);
      if (lookedUpRows?.length) {
        openWarningPopup(lookedUpRows);
        nextLookupRows = lookedUpRows;
      }
    }

    setScreenState((prev) => {
      const manualRows = prev.rows.filter((row) => row.sourceType === "manual");
      const mergedRows = [...nextLookupRows, ...manualRows];
      return {
        ...prev,
        importedFileName,
        rows: recalculateGroupedVariance(mergedRows),
      };
    });

    setSelectedIds([]);
    resetFileRef.current?.();
    NotificationExtension.Success(`Đã nạp ${parsedRows.length} serial từ file storecheck.`);
  };

  const handleConfirmSheetImport = async () => {
    if (!importWorkbook) {
      NotificationExtension.Warn("Không có dữ liệu workbook để import.");
      return;
    }
    if (!selectedImportSheet) {
      NotificationExtension.Warn("Vui lòng chọn sheet để import.");
      return;
    }

    open();
    try {
      const content = getSheetTextFromWorkbook(importWorkbook, selectedImportSheet);
      const parsedRows = parseStorecheckContent(content);
      const fileLabel = pendingImportFileName
        ? `${pendingImportFileName} - ${selectedImportSheet}`
        : selectedImportSheet;
      await applyImportedRows(parsedRows, fileLabel);
      closeImportSheetModal();
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Không thể import sheet đã chọn.");
    } finally {
      close();
    }
  };
  const handleImportStorecheck = async (file: File | null) => {
    if (!file) {
      return;
    }

    const fileName = file.name.toLowerCase();
    const isTextFile = fileName.endsWith(".csv") || fileName.endsWith(".txt");
    const isExcelFile = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (!isTextFile && !isExcelFile) {
      NotificationExtension.Fails("Chỉ hỗ trợ file storecheck dạng CSV, TXT, XLSX hoặc XLS.");
      resetFileRef.current?.();
      return;
    }

    open();

    try {
      if (isExcelFile) {
        const workbook = await readExcelWorkbook(file);
        const sheetNames = workbook.SheetNames || [];

        if (!sheetNames.length) {
          NotificationExtension.Warn("File Excel không có sheet dữ liệu.");
          return;
        }

        if (sheetNames.length > 1) {
          setImportWorkbook(workbook);
          setImportSheetNames(sheetNames);
          setSelectedImportSheet(sheetNames[0]);
          setPendingImportFileName(file.name);
          openSheetModal();
          return;
        }

        const sheetName = sheetNames[0];
        const content = getSheetTextFromWorkbook(workbook, sheetName);
        const parsedRows = parseStorecheckContent(content);
        await applyImportedRows(parsedRows, `${file.name} - ${sheetName}`);
        return;
      }

      const content = await readFileText(file);
      const parsedRows = parseStorecheckContent(content);
      await applyImportedRows(parsedRows, file.name);
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Không thể đọc file storecheck.");
    } finally {
      close();
    }
  };
  const buildSuccessRow = React.useCallback(
    (row: ScanRow, data: LookupApiResponse) => {
      const resolvedScanCount = Math.max(0, row.scanCount);
      const expectedQuantity = Math.max(
        0,
        pickNumber(data.sumQuantity, data.quantity, row.expectedQuantity, 0)
      );
      const variance = pickNumber(
        data.difference,
        data.chenhLech,
        resolvedScanCount - expectedQuantity
      );

      return {
        ...row,
        itemCode: pickText(data.itemCode, data.maVt, row.itemCode),
        itemName: pickText(data.itemName, data.tenVt, row.itemName),
        condition:
          pickText(row.condition, data.serialStatus, data.tinhTrang) ||
          DEFAULT_CONDITION_OPTIONS[0].value,
        conditionNote: pickText(
          row.conditionNote,
          data.note,
          data.ghiChuTinhTrang
        ),
        stockAge: pickNumber(data.storageDays, data.thoiGianLuuKho, row.stockAge),
        expectedQuantity,
        scanCount: resolvedScanCount,
        variance,
        lookupStatus: "success" as const,
        errorMessage: pickText(data.statusMessage),
        warningMessage: pickText(data.warningMessage),
        isSaved: false,
        saveId: undefined,
      };
    },
    []
  );

  const buildErrorRow = React.useCallback((row: ScanRow, message: string) => {
    return {
      ...row,
      expectedQuantity: 0,
      variance: row.scanCount,
      lookupStatus: "error" as const,
      errorMessage: message,
      warningMessage: "",
      isSaved: false,
      saveId: undefined,
    };
  }, []);

  const buildLookupFormData = React.useCallback((serials: string[]) => {
    const formData = new FormData();

    formData.append("MaKho", screenState.subInvCode);

    serials.forEach((serial) => {
      formData.append("Serials", serial);
    });

    return formData;
  }, [screenState.subInvCode]);

  const openWarningPopup = React.useCallback((rows: ScanRow[]) => {
    // Popup cảnh báo tạm thời được ẩn theo yêu cầu
  }, []);

  const lookupOneRow = React.useCallback(async (row: ScanRow) => {
    if (row.sourceType === "manual") {
      return {
        ...row,
        lookupStatus: "manual" as const,
        expectedQuantity: 0,
        variance: row.scanCount,
      };
    }

    if (!screenState.subInvCode) {
      return buildErrorRow(row, "Vui lòng chọn kho kiểm kê trước khi quét serial.");
    }

    const formData = buildLookupFormData([row.serial]);

    const response = await repositoryPos.post<MessageResponse<LookupApiResult>>(
      STOCKTAKING_ENDPOINTS.lookup,
      formData,
      true
    );

    const data = getLookupDataItem(response?.data);

    if (!response?.success || !data) {
      return buildErrorRow(
        row,
        response?.message || "Không tìm thấy serial trong hệ thống."
      );
    }


    if (!hasPosInventoryData(data)) {
      return buildErrorRow(
        row,
        pickText(data.statusMessage) ||
        "Serial không có tồn trong POS. Vui lòng nhập ở ô 'Quét serial không có tồn trên POS'."
      );
    }

    return buildSuccessRow(row, data);
  }, [buildErrorRow, buildLookupFormData, buildSuccessRow, screenState.subInvCode]);

  const lookupByImportedFile = React.useCallback(
    async (sourceRows: ScanRow[]) => {
      if (!screenState.subInvCode) {
        NotificationExtension.Warn("Vui lòng chọn kho kiểm kê trước khi tra cứu.");
        return null;
      }

      const formData = buildLookupFormData(sourceRows.map((row) => row.serial));

      const response = await repositoryPos.post<MessageResponse<LookupApiResult>>(
        STOCKTAKING_ENDPOINTS.lookup,
        formData,
        true
      );

      const dataList = getLookupDataList(response?.data);

      if (!response?.success || !dataList.length) {
        return null;
      }

      const dataMap = new Map(
        dataList
          .map(
            (item): [string, LookupApiResponse] => [
              normalizeSerial(pickText(item.serial, item.serialCode)),
              item,
            ]
          )
          .filter(([serial]) => serial)
      );

      return sourceRows.map((row) => {
        const matchedData = dataMap.get(normalizeSerial(row.serial));

        if (!matchedData) {
          return buildErrorRow(
            row,
            "Không tìm thấy serial trong kết quả trả về từ file."
          );
        }


        if (!hasPosInventoryData(matchedData)) {
          return buildErrorRow(
            row,
            pickText(matchedData.statusMessage) ||
            "Serial không có tồn trong POS. Vui lòng nhập ở ô 'Quét serial không có tồn trên POS'."
          );
        }

        return buildSuccessRow(row, matchedData);
      });
    },
    [
      buildErrorRow,
      buildLookupFormData,
      buildSuccessRow,
      screenState.subInvCode,
    ]
  );

  const applyLookupResults = React.useCallback((nextRows: ScanRow[]) => {
    setScreenState((prev) => ({
      ...prev,
      rows: recalculateGroupedVariance(nextRows),
    }));
  }, []);

  const handleLookupRows = React.useCallback(
    async (targetIds?: string[]) => {
      const sourceRows = screenState.rows.filter((row) => {
        if (targetIds?.length) {
          return targetIds.includes(row.id);
        }

        return row.sourceType === "lookup";
      });

      if (!sourceRows.length) {
        NotificationExtension.Warn("Không có serial cần tra cứu.");
        return;
      }

      if (!targetIds?.length && !screenState.subInvCode) {
        NotificationExtension.Warn("Vui lòng chọn kho kiểm kê.");
        return;
      }

      open();

      try {
        const rowMap = new Map(screenState.rows.map((item) => [item.id, item]));
        const lookedUpRows =
          (!targetIds?.length && (await lookupByImportedFile(sourceRows))) ||
          (await Promise.all(sourceRows.map(lookupOneRow)));
        const successCount = lookedUpRows.filter(
          (item) => item.lookupStatus === "success"
        ).length;
        const errorCount = lookedUpRows.filter(
          (item) => item.lookupStatus === "error"
        ).length;

        lookedUpRows.forEach((row) => {
          rowMap.set(row.id, row);
        });

        const nextRows = screenState.rows.map((row) => rowMap.get(row.id) || row);

        applyLookupResults(nextRows);
        openWarningPopup(lookedUpRows);
        if (successCount > 0 && errorCount === 0) {
          NotificationExtension.Success(
            `Đã tra cứu thành công ${successCount} serial.`
          );
        } else if (successCount > 0) {
          NotificationExtension.Warn(
            `Đã tra cứu ${successCount} serial, ${errorCount} serial lỗi.`
          );
        } else {
          NotificationExtension.Fails("Không tra cứu được serial nào.");
        }
      } finally {
        close();
      }
    },
    [
      applyLookupResults,
      lookupByImportedFile,
      lookupOneRow,
      openWarningPopup,
      open,
      close,
      screenState.rows,
      screenState.subInvCode,
    ]
  );

  const handleAddExisting = async () => {
    const normalizedSerial = normalizeSerial(serialInput);

    if (!normalizedSerial) {
      NotificationExtension.Fails("Vui lòng nhập serial cần quét.");
      return;
    }

    const row = createLookupRow({
      serial: normalizedSerial,
      scanCount: 1,
      condition: serialCondition,
      conditionNote: serialConditionNote.trim(),
      sourceRows: [],
    });

    open();

    try {
      const rowsToLookup = [row];
      const lookedUpRows = await lookupByImportedFile(rowsToLookup);
      const lookedUpRow = lookedUpRows?.find(
        (item) => normalizeSerial(item.serial) === normalizedSerial
      );

      if (!lookedUpRows?.length || lookedUpRow?.lookupStatus !== "success") {
        NotificationExtension.Fails(
          lookedUpRow?.errorMessage || "Không tìm thấy serial trong POS."
        );
        return;
      }

      upsertRows([lookedUpRow]);
      openWarningPopup(lookedUpRow ? [lookedUpRow] : []);
      setSerialInput("");
      setSerialCondition(
        conditionOptions[0]?.value || DEFAULT_CONDITION_OPTIONS[0].value
      );
      setSerialConditionNote("");
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Không thể tra cứu serial trong POS.");
    } finally {
      close();
    }
  };

  const handleResolveMissingItemCode = React.useCallback(async () => {
    const itemCode = missingInput.itemCode.trim().toUpperCase();
    if (!itemCode) return;

    setIsResolvingItemName(true);
    try {
      const itemName = await resolveItemByCode(itemCode);
      if (!itemName) {
        NotificationExtension.Fails("Mã vật tư không tồn tại trên hệ thống.");
        return;
      }

      setMissingInput((prev) => ({
        ...prev,
        itemCode,
        itemName,
      }));
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Không thể kiểm tra mã vật tư.");
    } finally {
      setIsResolvingItemName(false);
    }
  }, [missingInput.itemCode, resolveItemByCode]);

  const handleAddMissing = async () => {
    const normalizedSerial = normalizeSerial(missingInput.serial);
    const itemCode = missingInput.itemCode.trim().toUpperCase();
    let itemName = missingInput.itemName.trim();

    if (!normalizedSerial) {
      NotificationExtension.Fails("Vui lòng nhập serial.");
      return;
    }

    if (itemCode) {
      const resolved = await resolveItemByCode(itemCode);
      if (!resolved) {
        NotificationExtension.Fails("Mã vật tư không tồn tại trên hệ thống.");
        return;
      }
      itemName = itemName || resolved;
    }

    if (!itemName) {
      NotificationExtension.Fails("Vui lòng nhập tên vật tư.");
      return;
    }

    upsertRows([
      {
        id: createRowId(),
        sourceType: "manual",
        serial: normalizedSerial,
        itemCode,
        itemName,
        condition: missingInput.condition,
        conditionNote: missingInput.conditionNote.trim(),
        stockAge: 0,
        expectedQuantity: 0,
        scanCount: 1,
        variance: 1,
        lookupStatus: "manual",
        warningMessage: "",
        errorMessage: "",
        sourceRows: [],
        isSaved: false,
      },
    ]);

    setMissingInput({
      serial: "",
      itemCode: "",
      itemName: "",
      condition:
        conditionOptions[2]?.value || DEFAULT_CONDITION_OPTIONS[2].value,
      conditionNote: "",
    });

    NotificationExtension.Success("Đã thêm serial không có tồn trên POS.");
  };
  const handleDeleteSelected = async () => {
    if (!screenState.rows.length) {
      NotificationExtension.Warn("Không có dữ liệu để xóa.");
      return;
    }

    const deleteCount = selectedIds.length || screenState.rows.length;
    const confirmMessage = selectedIds.length
      ? `Bạn có chắc chắn muốn xóa ${deleteCount} dòng đã chọn?`
      : "Bạn có chắc chắn muốn xóa toàn bộ dữ liệu kiểm kê trên màn hình?";

    modals.openConfirmModal({
      title: "Xóa dữ liệu kiểm kê",
      children: <Text size="sm">{confirmMessage}</Text>,
      labels: { confirm: "Xóa", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        setScreenState((prev) => ({
          ...prev,
          completedAt: null,
          importedFileName: selectedIds.length ? prev.importedFileName : "",
          rows: selectedIds.length
            ? prev.rows.filter((row) => !selectedIds.includes(row.id))
            : [],
        }));
        setSelectedIds([]);
        NotificationExtension.Success(`Đã xóa ${deleteCount} dòng dữ liệu.`);
      },
    });
  };

  const handleSaveToApi = React.useCallback(async () => {
    const rowsToSave = screenState.rows.filter(
      (item) => item.lookupStatus === "success" || item.lookupStatus === "manual"
    );

    if (!rowsToSave.length) {
      NotificationExtension.Warn("Không có dữ liệu hợp lệ để lưu API.");
      return;
    }

    open();

    try {
      const payload = rowsToSave.map((row) => ({
        id: 0,
        maVt: row.itemCode,
        tenVt: row.itemName,
        serial: row.serial,
        tinhTrang: row.condition,
        ghiChuTinhTrang: row.conditionNote,
        thoiGianLuuKho: row.stockAge,
        slDaQuet: row.scanCount,
        chenhLech: row.variance,
      }));

      const response = await repositoryPos.post<MessageResponse<any>>(
        STOCKTAKING_ENDPOINTS.save,
        payload
      );

      if (!response) {
        return;
      }

      setScreenState((prev) => ({
        ...prev,
        completedAt: new Date().toISOString(),
        importedFileName: "",
        rows: [],
      }));
      setSelectedIds([]);

      NotificationExtension.Success(`Đã lưu ${rowsToSave.length} dòng lên API.`);
    } finally {
      close();
    }
  }, [screenState.rows, open, close]);

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const canDeleteRows = screenState.rows.length > 0;

  return (
    <>
      <LoadingOverlayCustom visible={visible} />
      <Modal
        opened={sheetModalOpened}
        onClose={closeImportSheetModal}
        title="Chọn sheet import"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            File: <b>{pendingImportFileName || "Excel"}</b>
          </Text>
          <Select
            label="Sheet"
            data={importSheetNames.map((sheet) => ({ value: sheet, label: sheet }))}
            value={selectedImportSheet || null}
            onChange={(value) => setSelectedImportSheet(value || "")}
            searchable
            placeholder="Chọn sheet cần import"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeImportSheetModal}>
              Hủy
            </Button>
            <Button leftSection={<IconCheck size={16} />} onClick={handleConfirmSheetImport}>
              Xác nhận
            </Button>
          </Group>
        </Stack>
      </Modal>

      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "0 10px",
        }}
      >
        <_breadcrumb />
      </div>

      <Stack gap="sm" mt="sm">
        <Paper withBorder radius="md" p="md">
          <Title order={3} ta="center" mb="md">
            Kiểm kê serial POS
          </Title>

          <Group justify="end" gap="sm" mb="sm" wrap="wrap">
            <Box w={140}>
              <Select
                placeholder="Tháng kiểm kê"
                data={MONTH_OPTIONS}
                value={String(screenState.kyKiemKe)}
                allowDeselect={false}
                onChange={(value) =>
                  setScreenState((prev) => ({
                    ...prev,
                    kyKiemKe: normalizeInventoryMonth(value),
                  }))
                }
              />
            </Box>
            <Box miw={280}>
              <Select
                placeholder="Chọn kho kiểm kê"
                searchable
                clearable
                leftSection={<IconBuildingWarehouse size={16} />}
                data={warehouseOptions}
                value={screenState.subInvCode || null}
                onDropdownOpen={() => {
                  void fetchWarehouseOptions();
                }}
                onChange={(value) =>
                  setScreenState((prev) => ({
                    ...prev,
                    subInvCode: value || "",
                  }))
                }
              />
            </Box>
            {/* 
            <Button
              variant="outline"
              color="teal"
              leftSection={<IconDownload size={16} />}
              onClick={buildTemplateSheet}
              style={actionButtonStyle}
            >
              Tải file mẫu
            </Button> */}

            <FileButton
              resetRef={resetFileRef}
              accept=".csv,.txt,.xlsx,.xls"
              onChange={handleImportStorecheck}
            >
              {(props) => (
                <Button
                  variant="outline"
                  color="blue"
                  leftSection={<IconFileUpload size={16} />}
                  style={actionButtonStyle}
                  {...props}
                >
                  Import File
                </Button>
              )}
            </FileButton>

            {/* <Button
              variant="outline"
              color="cyan"
              leftSection={<IconSearch size={16} />}
              onClick={() => handleLookupRows()}
              style={actionButtonStyle}
            >
              Tra cứu serial
            </Button> */}

            <Button
              variant="outline"
              color="teal"
              leftSection={<IconCheck size={16} />}
              onClick={() => {
                void handleSaveToApi();
              }}
              style={actionButtonStyle}
            >
              Hoàn thành
            </Button>

            <Button
              variant="outline"
              color="gray"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                void handleDeleteSelected();
              }}
              disabled={!canDeleteRows}
              style={actionButtonStyle}
            >
              {selectedIds.length
                ? `Xóa đã chọn (${selectedIds.length})`
                : "Xóa dữ liệu"}
            </Button>

          </Group>

          <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
            <Text size="sm" c="dimmed">
              File đã nạp:{" "}
              <b>{screenState.importedFileName || "Chưa nạp file storecheck"}</b>
            </Text>
            <Text size="sm" c="dimmed">
              Định dạng file quét: <b>serial, số lượng</b>. Nếu file chỉ có
              serial, hệ thống mặc định số lượng = 1.
            </Text>
          </Group>

          <Box
            style={{
              border: "1px solid #9aa8c3",
              overflow: "hidden",
            }}
          >
            <Grid columns={24} gutter={0}>
              <Grid.Col span={{ base: 24, md: 3 }}>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  Quét serial
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 24, md: 7 }}>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    leftSection={<IconBarcode size={16} />}
                    placeholder="Nhập hoặc quét serial"
                    value={serialInput}
                    onChange={(event) => setSerialInput(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAddExisting();
                      }
                    }}
                  />
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 24, md: 3 }}>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  Tình trạng
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 24, md: 4 }}>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <Select
                    variant="unstyled"
                    style={{ flex: 1 }}
                    comboboxProps={{ withinPortal: false }}
                    data={conditionOptions}
                    value={serialCondition}
                    onChange={(value) =>
                      setSerialCondition(
                        value ||
                        conditionOptions[0]?.value ||
                        DEFAULT_CONDITION_OPTIONS[0].value
                      )
                    }
                  />
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 24, md: 3 }}>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                  }}
                >
                  Ghi chú tình trạng hàng hóa
                </Box>
              </Grid.Col>
              <Grid.Col span={{ base: 24, md: 4 }}>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    placeholder="Nhập ghi chú tình trạng"
                    value={serialConditionNote}
                    onChange={(event) =>
                      setSerialConditionNote(event.currentTarget.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAddExisting();
                      }
                    }}
                  />
                </Box>
              </Grid.Col>
            </Grid>

            <Box
              style={{
                ...dividerStyle,
                borderBottom: "1px solid #9aa8c3",
              }}
            />

            <Box style={{ overflowX: "auto" }}>
              <Box style={singleLineGridStyle}>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "nowrap",
                  }}
                >
                  Quét serial không có tồn trên POS
                </Box>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    placeholder="Nhập serial"
                    value={missingInput.serial}
                    onChange={(event) =>
                      setMissingInput((prev) => ({
                        ...prev,
                        serial: event.currentTarget.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAddMissing();
                      }
                    }}
                  />
                </Box>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "nowrap",
                  }}
                >
                  Mã VT
                </Box>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    placeholder="Mã vật tư"
                    value={missingInput.itemCode}
                    onChange={(event) => {
                      const nextItemCode = event.currentTarget.value;
                      setMissingInput((prev) => ({
                        ...prev,
                        itemCode: nextItemCode,
                        itemName: nextItemCode.trim() ? "" : prev.itemName,
                      }));
                    }}
                    onBlur={() => {
                      void handleResolveMissingItemCode();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleResolveMissingItemCode();
                      }
                    }}
                    rightSection={isResolvingItemName ? "..." : null}
                  />
                </Box>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "nowrap",
                  }}
                >
                  Tên VT
                </Box>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    placeholder="Tên vật tư"
                    value={missingInput.itemName}
                    onChange={(event) =>
                      setMissingInput((prev) => ({
                        ...prev,
                        itemName: event.currentTarget.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAddMissing();
                      }
                    }}
                  />
                </Box>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "nowrap",
                  }}
                >
                  Tình trạng
                </Box>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <Select
                    variant="unstyled"
                    style={{ flex: 1 }}
                    comboboxProps={{ withinPortal: false }}
                    data={conditionOptions}
                    value={missingInput.condition}
                    onChange={(value) =>
                      setMissingInput((prev) => ({
                        ...prev,
                        condition:
                          value ||
                          conditionOptions[2]?.value ||
                          DEFAULT_CONDITION_OPTIONS[2].value,
                      }))
                    }
                  />
                </Box>
                <Box
                  style={{
                    ...headerCellStyle,
                    borderRight: "1px solid #9aa8c3",
                    borderBottom: "1px solid #9aa8c3",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ghi chú tình trạng
                </Box>
                <Box
                  style={{
                    ...valueCellStyle,
                    borderBottom: "1px solid #9aa8c3",
                  }}
                >
                  <TextInput
                    variant="unstyled"
                    style={{ flex: 1 }}
                    placeholder="Nhập ghi chú"
                    value={missingInput.conditionNote}
                    onChange={(event) =>
                      setMissingInput((prev) => ({
                        ...prev,
                        conditionNote: event.currentTarget.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAddMissing();
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box style={dividerStyle} />
          </Box>
        </Paper>

        <Paper withBorder radius="md" p="md">
          <ScrollArea h={520}>
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              stickyHeader
              styles={{
                th: {
                  fontSize: rem(13),
                  padding: "10px 10px",
                  whiteSpace: "nowrap",
                },
                td: {
                  fontSize: rem(13),
                  padding: "10px 10px",
                },
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>STT</Table.Th>
                  <Table.Th>Mã VT</Table.Th>
                  <Table.Th>Tên VT</Table.Th>
                  <Table.Th>Serial</Table.Th>
                  <Table.Th>Tình trạng</Table.Th>
                  <Table.Th>Thời gian lưu kho</Table.Th>
                  <Table.Th>SL đã quét</Table.Th>
                  <Table.Th>Chênh lệch sản phẩm có tồn</Table.Th>
                  <Table.Th>Chênh lệch sản phẩm không tồn</Table.Th>
                  <Table.Th ta="center">Lịch sử</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {screenState.rows.length ? (
                  screenState.rows.map((row, index) => {
                    const isMissingSerial = row.sourceType === "manual";
                    const isSelected = selectedIds.includes(row.id);
                    const rowBackground = isSelected
                      ? "#edf4ff"
                      : isMissingSerial
                        ? "#fff1f0"
                        : undefined;
                    const disabledCellStyle: React.CSSProperties = {
                      backgroundColor: "#f1f3f5",
                      color: "#adb5bd",
                      fontWeight: 700,
                    };

                    return (
                      <Table.Tr
                        key={row.id}
                        onClick={() => toggleRowSelection(row.id)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: rowBackground,
                        }}
                      >
                        <Table.Td>{index + 1}</Table.Td>
                        <Table.Td>{row.itemCode}</Table.Td>
                        <Table.Td>{row.itemName}</Table.Td>
                        <Table.Td>{row.serial}</Table.Td>
                        <Table.Td>{row.condition}</Table.Td>
                        <Table.Td>{row.stockAge}</Table.Td>
                        <Table.Td>{row.scanCount}</Table.Td>
                        <Table.Td
                          style={
                            isMissingSerial
                              ? disabledCellStyle
                              : {
                                color:
                                  row.variance !== 0 ? "#c92a2a" : "#2b8a3e",
                                fontWeight: 700,
                              }
                          }
                        >
                          {isMissingSerial ? "-" : row.variance}
                        </Table.Td>
                        <Table.Td
                          style={
                            isMissingSerial
                              ? {
                                color:
                                  row.scanCount !== 0 ? "#c92a2a" : "#2b8a3e",
                                fontWeight: 700,
                              }
                              : disabledCellStyle
                          }
                        >
                          {isMissingSerial ? row.scanCount : "-"}
                        </Table.Td>
                        <Table.Td ta="center">
                          <Tooltip label="Tra cứu lịch sử serial">
                            <ActionIcon
                              variant="light"
                              color="blue"
                              onClick={(event) => {
                                event.stopPropagation();
                                goToSerialHistory(row.serial);
                              }}
                            >
                              <IconHistory size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={10}>
                      <Text ta="center" c="dimmed" py="lg">
                        Chưa có serial nào được quét.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Stack>
    </>
  );
};

export default SerialStocktake;
